import { userMessage } from '@rr-crm/shared/api-errors';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { apiFetch, throwIfNotOk } from '../api/client';
import { hrefForResult, SearchResults, type SearchResultRow } from './SearchResults';

export { hrefForResult };

/**
 * T9-017 — Global search bar (sticky top-of-app).
 *
 * Mobile-first input + ranked-results dropdown with keyboard navigation:
 *   Arrow ↓/↑ — move focus through results
 *   Enter      — navigate to the focused result
 *   Esc        — close the dropdown
 *
 * Debounce: 250ms — short enough that the perceived latency is "instant"
 * for typists, long enough to skip the per-keystroke hit on the GIN index
 * during fast burst-typing.
 *
 * Backend: GET /api/v1/search?q=...
 */

const DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

export function GlobalSearchBar(): JSX.Element {
  const navigate = useNavigate();
  const [q, setQ] = useState<string>('');
  const [results, setResults] = useState<SearchResultRow[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounced search.
  useEffect(() => {
    const trimmed = q.trim();
    if (trimmed.length < MIN_QUERY_LEN) {
      setResults([]);
      setOpen(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    // RIG-490 — `setTimeout(async …)` hands the callback's promise to a
    // caller that ignores it, so a throw inside became an unhandled
    // rejection. The body already try/catches; `void` states that the
    // result is deliberately dropped rather than forgotten.
    const timeout = setTimeout(() => {
      void (async () => {
        try {
          const resp = await apiFetch(`/api/v1/search?q=${encodeURIComponent(trimmed)}`);
          // QA 2026-10-01 F-28: plain sentence, not "Search failed: 500".
          await throwIfNotOk(resp);
          const data = (await resp.json()) as {
            results: SearchResultRow[];
            queryMs: number;
          };
          if (cancelled) return;
          setResults(data.results);
          setError(null);
          setOpen(true);
          setFocusedIndex(-1);
        } catch (err) {
          if (!cancelled) {
            setError(`Search isn't working right now. ${userMessage(err)}`);
            setOpen(true);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [q]);

  // Click-outside dismisses.
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (results.length === 0) return;
      setOpen(true);
      setFocusedIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setFocusedIndex((i) => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      if (focusedIndex >= 0 && results[focusedIndex]) {
        navigateToResult(results[focusedIndex]);
        setOpen(false);
        setQ('');
      }
    } else if (e.key === 'Escape') {
      setOpen(false);
      inputRef.current?.blur();
    }
  };

  const navigateToResult = (result: SearchResultRow): void => {
    // M22 — was a full-page window.location reload (re-bootstraps MSAL,
    // re-fetches profile, re-mounts the SPA). react-router IS wired here, so
    // push client-side. RIG-478 — an unopenable row is no longer offered as
    // clickable at all, so reaching here with a null href is not expected;
    // leaving the dropdown open is the honest response if it happens.
    const href = hrefForResult(result);
    if (href === null) return;
    setOpen(false);
    navigate(href);
  };

  return (
    <div ref={containerRef} className="relative w-full max-w-lg">
      <input
        ref={inputRef}
        type="search"
        role="searchbox"
        aria-label="Search across accounts, contacts, deals, and activities"
        placeholder="Search…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => {
          if (results.length > 0) setOpen(true);
        }}
        onKeyDown={handleKeyDown}
        className="w-full rounded-md border border-rr-border-default bg-rr-surface px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-rr-teal-light"
      />
      {open && (
        <SearchResults
          results={results}
          loading={loading}
          error={error}
          focusedIndex={focusedIndex}
          onPick={(r) => {
            if (hrefForResult(r) === null) return;
            navigateToResult(r);
            setOpen(false);
            setQ('');
          }}
        />
      )}
    </div>
  );
}

